<template>
  <div v-if="showSearch" class="mb-2">
    <PrimeVueFloatLabel variant="on">
      <PrimeVueIconField>
        <PrimeVueInputText
          id="search"
          v-model="facetQuery"
          type="search"
          name="search"
          size="small"
          suffix-icon="search"
          :label="searchLabel"
          :disabled="store.loading"
          class="w-100"
          autocomplete="off"
        />
        <PrimeVueInputIcon>
          <icon :name="'action_bar_magnifier'" />
        </PrimeVueInputIcon>
      </PrimeVueIconField>
      <label for="search">{{ searchLabel }}</label>
    </PrimeVueFloatLabel>
  </div>

  <ExpandableInputList
    :type="props.filterType"
    :label="props.filterLabel"
    :name="props.filterName"
    :value="props.filterValue"
    :options="options"
    :displayed-options="optionsToDisplay"
    :show-more-label="
      $textDictionary['productsList.filters.button.showmore.label']
    "
    :show-less-label="
      $textDictionary['productsList.filters.button.showless.label']
    "
    :disabled="store.loading"
    @change="(name, value) => store.handleFormValueChange(name, value)"
  />
</template>

<script setup lang="ts">
import { Icon } from '@hypercodestudio/basler-components';
import { useProductFilterStore } from '~/stores/productFilterStore/productFilterStore';
import ExpandableInputList from './ExpandableInputList.vue';

const { $textDictionary } = useNuxtApp();
const store = useProductFilterStore();

interface Props {
  filterType: 'checkbox' | 'radio';
  filterLabel: string;
  filterName: string;
  filterValue: string[];
  filterOptions: Record<string, string>;
}

const props = defineProps<Props>();

// TODO: make configurable?!
const optionsToDisplay = 8;

const facetQuery = ref('');
const showSearch = ref(false);
const facetSearchResult = ref<string[] | null>(null);

const searchLabel = computed(() => {
  return (
    $textDictionary.value['productsList.filter.search.placeholder']?.replace(
      '{category}',
      props.filterLabel
    ) ?? 'productsList.filter.search.placeholder'
  );
});

const options = computed<Record<string, string>>(() => {
  const optionsWithLabels: Record<string, string> = {};

  // we always show active filters as a facet search might hide those
  // (or they are not included in the default filters).
  /*activeValues.value?.forEach((value) => {
    if (!optionsWithLabels[value]) {
      optionsWithLabels[value] = useFilterValueLabel(props.filterName, value);
    }
  });*/

  if (facetSearchResult.value) {
    facetSearchResult.value.forEach((facetValue) => {
      Object.entries(props.filterOptions).forEach(
        ([filterKey, filterValue]) => {
          if (facetValue === filterKey) {
            optionsWithLabels[filterKey] = filterValue;
          }
        }
      );
    });
  } else {
    Object.entries(props.filterOptions).forEach(([filterKey, filterValue]) => {
      optionsWithLabels[filterKey] = filterValue;
    });
  }
  if (Object.entries(optionsWithLabels).length > optionsToDisplay) {
    showSearch.value = true;
  }

  return optionsWithLabels;
});

const autocompleteOptions = (options: { [key: string]: string }) => {
  const items = [];

  for (const [key] of Object.entries(options)) {
    items.push(key);
  }
  return items;
};

watch(
  facetQuery,
  debounce(async () => {
    if (!facetQuery.value.trim()) {
      facetSearchResult.value = null;

      return;
    }
    facetSearchResult.value = autocompleteOptions(props.filterOptions).filter(
      (item) => {
        return item.toLowerCase().includes(facetQuery.value.toLowerCase());
      }
    );
  }, 300)
);
</script>
